import { useCallback, useMemo, useState } from 'react';
import { pipe } from 'fp-ts/function';
import { array, string } from 'fp-ts';
import { getId, lt, showSuccessAlert } from '@mews/framework';
import { Reservation } from '@mews-commander/data-access';
import { useResourceCategoriesByServiceId } from '@mews-commander/legacy-entities/src/utils/useResourceCategory';
import { useGainsightCommanderEvents } from '@mews-commander/utils';
import {
    ReservationChangeValidationState,
    ValidateReservationChangesResult,
    useValidateReservationChanges,
} from '../../../utils/useValidateReservationChanges';
import { useServiceAvailabilityQueries } from '../../../../availability';
import { useChangeAssignedCategoryMutation } from './useChangeAssignedCategoryMutation';
import { AssignedCategoryFormValues, AssignedCategoryFormKeys } from './types';

export const useChangeResourceCategory = (serviceId: string, reservations: Reservation[], scope: string) => {
    const gainsightEvents = useGainsightCommanderEvents();

    const [validationResult, setValidationResult] = useState<ValidateReservationChangesResult | null>(null);
    const resourceCategories = useResourceCategoriesByServiceId(serviceId);

    const reservationIds = reservations.map(getId);

    const changeAssignedCategoryMutation = useChangeAssignedCategoryMutation(reservationIds, scope);

    const availabilitiesResult = useServiceAvailabilityQueries(
        serviceId,
        reservations.map((r) => ({ startUtc: r.startUtc, endUtc: r.endUtc }))
    );
    const availabilities = availabilitiesResult.flatMap((value) => value.data?.availabilities ?? []);

    const validate = useValidateReservationChanges(serviceId, reservationIds);

    const changeResourceCategory = useCallback(
        (formValues: AssignedCategoryFormValues) => {
            const selectedResourceCategoryId = formValues[AssignedCategoryFormKeys.assignedResourceCategoryId];
            const selectedResourceCategory = resourceCategories?.[selectedResourceCategoryId];
            const substitutes = { spaceCategory: selectedResourceCategory?.name ?? '' };

            const originalResourceCategoryIds = pipe(
                reservations,
                array.map((r) => r.requestedCategoryId),
                array.uniq(string.Eq)
            );

            const onSubmit = () => {
                changeAssignedCategoryMutation.mutate(
                    { ...formValues, updateBlockAvailability: !!reservations[0]?.availabilityBlockId },
                    {
                        onSuccess: () => {
                            showSuccessAlert({ title: lt('UpdateAssignedCategorySuccess') });
                            setValidationResult(null);
                            gainsightEvents.onApiSuccess({ context: 'AssignedCategoryChanged' });
                        },
                    }
                );
            };

            const onReject = () => setValidationResult(null);

            const isPlural = originalResourceCategoryIds.length === 1;

            const result = validate({
                onSubmit,
                onReject,
                availabilities,
                availabilityBlockId: reservations?.[0]?.availabilityBlockId,
                newResourceCategoryId: selectedResourceCategoryId,
                reservationsCount: reservations?.length,
                getCustomMessages: () => ({
                    [ReservationChangeValidationState.NoRightsToOverbook]: {
                        title: lt('UpdateAssignedCategoryOverbookingNoRightsTitle'),
                    },
                    [ReservationChangeValidationState.UpdatingAvailabilityBlock]: {
                        description: lt(
                            isPlural
                                ? 'UpdateAssignedCategoryUpdateBlockDialogDescription'
                                : 'UpdateAssignedCategoryUpdateBlockDialogDescriptionPlural',
                            substitutes
                        ),
                    },
                    [ReservationChangeValidationState.OverbookingAndUpdatingAvailabilityBlock]: {
                        description: (
                            <>
                                {lt('UpdateAssignedCategoryUpdateBlockAndOverbookDialogParagraph1', substitutes)}
                                <br />
                                {lt(
                                    isPlural
                                        ? 'UpdateAssignedCategoryUpdateBlockAndOverbookDialogParagraph2'
                                        : 'UpdateAssignedCategoryUpdateBlockAndOverbookDialogParagraph2Plural',
                                    substitutes
                                )}
                            </>
                        ),
                    },
                }),
            });

            setValidationResult(result);
        },
        [availabilities, changeAssignedCategoryMutation, gainsightEvents, reservations, resourceCategories, validate]
    );

    return useMemo(
        () => ({
            loading: changeAssignedCategoryMutation.isPending,
            changeResourceCategory,
            ...(validationResult ?? {}),
        }),
        [changeAssignedCategoryMutation.isPending, changeResourceCategory, validationResult]
    );
};
